import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getGuideSteps } from '../../features/dropshipping/utils/nav-data'
import { NavigationStep } from './GuideNav'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { normalizeLanguage } from '../../i18n/language'

interface PageNavProps {
    position?: 'top' | 'bottom'
    steps?: NavigationStep[]  // Optional: custom navigation steps (for admin, etc.)
    returnPath?: string        // Optional: custom return path
    returnText?: string        // Optional: custom return button text
    switchLabel?: string       // Optional: visible label for prev/next switch group
}

export function PageNav({
    position = 'bottom',
    steps,
    returnPath,
    returnText,
    switchLabel
}: PageNavProps) {
    const { i18n } = useTranslation()
    const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language)
    const location = useLocation()
    const pathname = location.pathname
    const localizedReturnText = returnText || (language === 'en' ? 'Back' : '返回')
    const localizedSwitchLabel = switchLabel || (language === 'en' ? 'Page switch' : '页面切换')
    const spacingClass = position === 'top' ? 'mb-0' : 'mt-0'

    // Use custom steps if provided, otherwise use default guideSteps
    const navigationSteps = steps || getGuideSteps(language === 'en' ? 'en' : 'zh')
    const backLink = returnPath || '/dropshipping'

    const currentIndex = navigationSteps.findIndex(step => step.href === pathname)
    const hasNext = currentIndex < navigationSteps.length - 1
    // If on the first step (index 0), previous link should be the same as backLink (return path)
    const previousHref = currentIndex > 0 ? navigationSteps[currentIndex - 1].href : (currentIndex === 0 ? backLink : null)
    const nextHref = hasNext ? navigationSteps[currentIndex + 1].href : null

    return (
        <div className={`w-full flex justify-between items-center gap-4 ${spacingClass}`}>
            <Link
                to={backLink}
                className="flex items-center justify-center gap-2 pl-2 pr-4 h-11 md:h-11 rounded-full bg-b text-w font-medium text-base hover:opacity-90 active:opacity-90"
            >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                {localizedReturnText}
            </Link>

            <div className="flex items-center gap-3">
                <span className="text-xs md:text-sm text-b4 whitespace-nowrap">{localizedSwitchLabel}</span>
                <div className="flex items-center rounded-full bg-w border border-b1 shadow-sm overflow-hidden">
                    {/* 左半部分：上一步 */}
                    {previousHref ? (
                        <Link
                            to={previousHref}
                            title={language === 'en' ? 'Previous management page' : '上一管理页'}
                            aria-label={language === 'en' ? 'Previous management page' : '上一管理页'}
                            className="flex items-center justify-center w-11 h-11 hover:bg-b02"
                        >
                            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                        </Link>
                    ) : (
                        <div className="w-11 h-11 opacity-30 flex items-center justify-center">
                            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                        </div>
                    )}

                    <div className="w-px h-6 bg-b1" />

                    {/* 右半部分：下一步 */}
                    {nextHref ? (
                        <Link
                            to={nextHref}
                            title={language === 'en' ? 'Next management page' : '下一管理页'}
                            aria-label={language === 'en' ? 'Next management page' : '下一管理页'}
                            className="flex items-center justify-center w-11 h-11 hover:bg-b02"
                        >
                            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                        </Link>
                    ) : (
                        <div className="w-11 h-11 opacity-30 flex items-center justify-center">
                            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
